/**
 * ConnectionDetailsModal Component
 * Displays detailed connection information including sync history and accounts
 * WP-FRONTEND-CONN-1: Connection Maintenance UI
 */

import { useState, useCallback } from 'react';
import Button from '@/components/ui/Button';
import ToggleSwitch from '@/components/ui/ToggleSwitch';
import { Alert } from '@/components/ui';
import ConnectionStatus from './ConnectionStatus';
import PlaidLinkButton from './PlaidLinkButton';
import {
  useConnectionDetails,
  useForceRefresh,
  useUpdateAccountVisibility,
} from '@/hooks/useConnections';
import { formatRelativeTime, formatCurrency, formatAccountMask, capitalize } from '@/utils/format';
import { cn } from '@/utils/cn';
import type { SyncHistoryEntry, SyncedAccountDetail, ConnectionDetail } from '@/types';

// ============================================================================
// Props and Types
// ============================================================================

export interface ConnectionDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  connectionId: string | null;
  onReconnect?: () => void;
}

type TabType = 'overview' | 'accounts' | 'history';

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get human-readable sync type label
 */
function getSyncTypeLabel(syncType: string): string {
  switch (syncType) {
    case 'full':
      return 'Full Sync';
    case 'incremental':
      return 'Incremental';
    case 'accounts_only':
      return 'Accounts Only';
    case 'force_refresh':
      return 'Force Refresh';
    default:
      return capitalize(syncType);
  }
}

/**
 * Get sync status styling
 */
function getSyncStatusStyle(status: string): { bg: string; text: string; label: string } {
  switch (status) {
    case 'success':
      return { bg: 'bg-green-100', text: 'text-green-800', label: 'Success' };
    case 'partial':
      return { bg: 'bg-amber-100', text: 'text-amber-800', label: 'Partial' };
    case 'failed':
      return { bg: 'bg-red-100', text: 'text-red-800', label: 'Failed' };
    case 'in_progress':
      return { bg: 'bg-blue-100', text: 'text-blue-800', label: 'In Progress' };
    default:
      return { bg: 'bg-gray-100', text: 'text-gray-800', label: capitalize(status) };
  }
}

/**
 * Calculate days until consent expires
 */
function getDaysUntilExpiration(expiresAt: string | null): number | null {
  if (!expiresAt) return null;
  const expDate = new Date(expiresAt);
  const now = new Date();
  const diffMs = expDate.getTime() - now.getTime();
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  return diffDays;
}

// ============================================================================
// Sub-components
// ============================================================================

/**
 * Consent expiration warning badge
 */
function ConsentExpirationBadge({
  consentExpiresAt,
  onRenew,
}: {
  consentExpiresAt: string | null;
  onRenew?: () => void;
}) {
  const daysUntil = getDaysUntilExpiration(consentExpiresAt);

  if (daysUntil === null || daysUntil > 30) {
    return null;
  }

  const isUrgent = daysUntil <= 7;
  const isExpired = daysUntil <= 0;

  return (
    <div
      className={cn(
        'flex items-center gap-3 p-3 rounded-lg',
        isExpired
          ? 'bg-red-50 border border-red-200'
          : isUrgent
            ? 'bg-amber-50 border border-amber-200'
            : 'bg-yellow-50 border border-yellow-200'
      )}
    >
      <WarningIcon
        className={cn(
          'h-5 w-5 flex-shrink-0',
          isExpired ? 'text-red-500' : isUrgent ? 'text-amber-500' : 'text-yellow-600'
        )}
      />
      <div className="flex-1 min-w-0">
        <p
          className={cn(
            'text-sm font-medium',
            isExpired ? 'text-red-800' : isUrgent ? 'text-amber-800' : 'text-yellow-800'
          )}
        >
          {isExpired
            ? 'Access has expired'
            : `Access expires in ${daysUntil} day${daysUntil === 1 ? '' : 's'}`}
        </p>
        <p className="text-xs text-gray-600 mt-0.5">
          {isExpired
            ? 'Renew access to continue syncing data.'
            : 'Renew access to avoid interruption.'}
        </p>
      </div>
      {onRenew && (
        <Button size="sm" variant={isExpired || isUrgent ? 'primary' : 'outline'} onClick={onRenew}>
          Renew Access
        </Button>
      )}
    </div>
  );
}

/**
 * Overview tab content
 */
function OverviewTab({
  connection,
  connectionId,
  onForceRefresh,
  isRefreshing,
  onRenew,
  onReconnectSuccess,
}: {
  connection: ConnectionDetail;
  connectionId: string;
  onForceRefresh: () => void;
  isRefreshing: boolean;
  onRenew?: () => void;
  onReconnectSuccess?: () => void;
}) {
  const needsReauth = connection.status === 'pending_reauth' || connection.status === 'error';
  const lastSync = connection.lastSyncAt ? formatRelativeTime(connection.lastSyncAt) : 'Never';

  return (
    <div className="space-y-6">
      {/* Consent expiration warning */}
      <ConsentExpirationBadge consentExpiresAt={connection.consentExpiresAt} onRenew={onRenew} />

      {/* Connection status */}
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-gray-50 rounded-lg p-4">
          <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Status</p>
          <div className="flex items-center gap-2">
            <ConnectionStatus status={connection.status} showText size="sm" />
          </div>
        </div>
        <div className="bg-gray-50 rounded-lg p-4">
          <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Last Sync</p>
          <p className="text-sm font-medium text-gray-900">{lastSync}</p>
        </div>
        <div className="bg-gray-50 rounded-lg p-4">
          <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Accounts</p>
          <p className="text-sm font-medium text-gray-900">{connection.accountsCount}</p>
        </div>
        <div className="bg-gray-50 rounded-lg p-4">
          <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Provider</p>
          <p className="text-sm font-medium text-gray-900 capitalize">{connection.provider}</p>
        </div>
      </div>

      {/* Error information */}
      {connection.lastError && (
        <Alert variant="error" className="text-sm">
          <div className="font-medium mb-1">
            {connection.lastErrorCode || 'Error'}
            {connection.lastErrorAt && (
              <span className="font-normal text-gray-600">
                {' '}
                - {formatRelativeTime(connection.lastErrorAt)}
              </span>
            )}
          </div>
          <p>{connection.lastError}</p>
        </Alert>
      )}

      {/* Force refresh section - only when connection is working */}
      {!needsReauth && (
        <div className="border-t border-gray-200 pt-4">
          <h4 className="text-sm font-medium text-gray-900 mb-2">Data Refresh</h4>
          <p className="text-sm text-gray-600 mb-4">
            Force a full refresh to re-sync all data from your institution. This clears the sync
            history and fetches fresh data. Use this when data appears stale or incomplete.
          </p>
          <Button
            variant="outline"
            onClick={onForceRefresh}
            isLoading={isRefreshing}
            disabled={isRefreshing || connection.status === 'pending'}
          >
            <RefreshIcon className="h-4 w-4 mr-2" />
            Force Refresh
          </Button>
        </div>
      )}

      {/* Reconnect section - always visible */}
      <div className="border-t border-gray-200 pt-4">
        <h4 className="text-sm font-medium text-gray-900 mb-2">
          {needsReauth ? 'Reconnect Required' : 'Re-authenticate Connection'}
        </h4>
        <p className="text-sm text-gray-600 mb-4">
          {needsReauth
            ? 'Your bank requires you to re-authenticate. This will open a secure login with your bank to restore the connection.'
            : 'Re-authenticate with your bank to refresh credentials. Use this if you\'ve changed your bank password, enabled new security features, or are experiencing connection issues.'}
        </p>
        <PlaidLinkButton
          connectionId={connectionId}
          variant={needsReauth ? 'primary' : 'outline'}
          size="md"
          onSuccess={onReconnectSuccess}
          leftIcon={<LinkIcon className="h-4 w-4" />}
        >
          {needsReauth ? 'Reconnect Now' : 'Re-authenticate'}
        </PlaidLinkButton>
      </div>

      {/* Initial sync incomplete warning */}
      {!connection.initialSyncComplete && (
        <Alert variant="info" className="text-sm">
          Initial sync is still in progress. Some data may not be available yet.
        </Alert>
      )}
    </div>
  );
}

/**
 * Single account row with visibility toggle
 */
function AccountRow({
  account,
  onToggleVisibility,
  isUpdating,
}: {
  account: SyncedAccountDetail;
  onToggleVisibility: (accountId: string, isHidden: boolean) => void;
  isUpdating: boolean;
}) {
  const handleToggle = useCallback(
    (enabled: boolean) => {
      // Toggle inverts the hidden state (enabled = visible = not hidden)
      onToggleVisibility(account.id, !enabled);
    },
    [account.id, onToggleVisibility]
  );

  const balance = account.balanceCurrent ?? account.balanceAvailable;
  const formattedBalance = balance !== null ? formatCurrency(balance, account.currency) : '--';

  return (
    <div
      className={cn(
        'flex items-center justify-between py-3 px-4 rounded-lg transition-colors',
        account.isHidden ? 'bg-gray-100 opacity-60' : 'bg-gray-50 hover:bg-gray-100'
      )}
    >
      <div className="flex items-center gap-3 min-w-0 flex-1">
        <AccountTypeIcon type={account.accountType} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="text-sm font-medium text-gray-900 truncate">{account.name}</p>
            {account.isHidden && (
              <span className="text-xs bg-gray-200 text-gray-600 px-1.5 py-0.5 rounded">
                Hidden
              </span>
            )}
          </div>
          <p className="text-xs text-gray-500">
            {capitalize(account.accountSubtype || account.accountType)}
            {account.mask && (
              <span className="ml-2 font-mono text-gray-400">
                {formatAccountMask(account.mask)}
              </span>
            )}
          </p>
        </div>
      </div>

      <div className="flex items-center gap-4 flex-shrink-0">
        <p
          className={cn(
            'text-sm font-semibold',
            balance !== null && balance < 0 ? 'text-red-600' : 'text-gray-900'
          )}
        >
          {formattedBalance}
        </p>
        <ToggleSwitch
          id={`account-visibility-${account.id}`}
          label={account.isHidden ? 'Show account' : 'Hide account'}
          enabled={!account.isHidden}
          onChange={handleToggle}
          disabled={isUpdating}
          isLoading={isUpdating}
          size="sm"
        />
      </div>
    </div>
  );
}

/**
 * Accounts tab content
 */
function AccountsTab({
  connection,
  onToggleVisibility,
  updatingAccountId,
}: {
  connection: ConnectionDetail;
  onToggleVisibility: (accountId: string, isHidden: boolean) => void;
  updatingAccountId: string | null;
}) {
  if (connection.accounts.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <p>No accounts found for this connection.</p>
      </div>
    );
  }

  const visibleAccounts = connection.accounts.filter((a: SyncedAccountDetail) => !a.isHidden);
  const hiddenAccounts = connection.accounts.filter((a: SyncedAccountDetail) => a.isHidden);

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600">
        Toggle account visibility to include or exclude accounts from your reports and Sankey
        diagrams.
      </p>

      {/* Visible accounts */}
      {visibleAccounts.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider">
            Visible Accounts ({visibleAccounts.length})
          </h4>
          {visibleAccounts.map((account: SyncedAccountDetail) => (
            <AccountRow
              key={account.id}
              account={account}
              onToggleVisibility={onToggleVisibility}
              isUpdating={updatingAccountId === account.id}
            />
          ))}
        </div>
      )}

      {/* Hidden accounts */}
      {hiddenAccounts.length > 0 && (
        <div className="space-y-2 mt-6">
          <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider">
            Hidden Accounts ({hiddenAccounts.length})
          </h4>
          {hiddenAccounts.map((account: SyncedAccountDetail) => (
            <AccountRow
              key={account.id}
              account={account}
              onToggleVisibility={onToggleVisibility}
              isUpdating={updatingAccountId === account.id}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Sync history entry row
 */
function SyncHistoryRow({ entry }: { entry: SyncHistoryEntry }) {
  const statusStyle = getSyncStatusStyle(entry.status);
  const startedAt = formatRelativeTime(entry.startedAt);
  const totalTxns = entry.transactionsAdded + entry.transactionsModified + entry.transactionsRemoved;

  return (
    <div className="flex items-start gap-3 py-3 border-b border-gray-100 last:border-0">
      {/* Status indicator */}
      <div
        className={cn(
          'flex-shrink-0 mt-0.5 h-2 w-2 rounded-full',
          entry.status === 'success'
            ? 'bg-green-500'
            : entry.status === 'partial'
              ? 'bg-amber-500'
              : entry.status === 'failed'
                ? 'bg-red-500'
                : 'bg-blue-500'
        )}
      />

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium text-gray-900">
            {getSyncTypeLabel(entry.syncType)}
          </span>
          <span
            className={cn(
              'text-xs px-1.5 py-0.5 rounded',
              statusStyle.bg,
              statusStyle.text
            )}
          >
            {statusStyle.label}
          </span>
          <span className="text-xs text-gray-500">{startedAt}</span>
        </div>

        {/* Stats */}
        {entry.status !== 'failed' && (
          <div className="flex gap-4 mt-1 text-xs text-gray-500">
            <span>{entry.accountsSynced} accounts</span>
            {totalTxns > 0 && (
              <span>
                {entry.transactionsAdded > 0 && `+${entry.transactionsAdded}`}
                {entry.transactionsModified > 0 && ` ~${entry.transactionsModified}`}
                {entry.transactionsRemoved > 0 && ` -${entry.transactionsRemoved}`}
                {' transactions'}
              </span>
            )}
          </div>
        )}

        {/* Error message */}
        {entry.errorMessage && (
          <p className="mt-1 text-xs text-red-600">
            {entry.errorCode && <span className="font-medium">{entry.errorCode}: </span>}
            {entry.errorMessage}
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * Sync history tab content
 */
function SyncHistoryTab({ connection }: { connection: ConnectionDetail }) {
  if (connection.syncHistory.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <ClockIcon className="h-8 w-8 mx-auto mb-2 text-gray-400" />
        <p>No sync history available.</p>
        <p className="text-sm mt-1">Sync history will appear after the first data sync.</p>
      </div>
    );
  }

  return (
    <div className="space-y-1">
      <p className="text-sm text-gray-600 mb-4">
        Recent sync operations (most recent first).
      </p>
      {connection.syncHistory.map((entry: SyncHistoryEntry) => (
        <SyncHistoryRow key={entry.id} entry={entry} />
      ))}
    </div>
  );
}

// ============================================================================
// Icons
// ============================================================================

function ClockIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}

function XIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      aria-hidden="true"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

function RefreshIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
      />
    </svg>
  );
}

function LinkIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244"
      />
    </svg>
  );
}

function WarningIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
      />
    </svg>
  );
}

function AccountTypeIcon({ type }: { type: string }) {
  const typeKey = type?.toLowerCase() ?? '';

  const getIcon = () => {
    switch (typeKey) {
      case 'depository':
      case 'checking':
      case 'savings':
        return (
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
          />
        );
      case 'credit':
        return (
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
          />
        );
      case 'investment':
        return (
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
          />
        );
      case 'loan':
        return (
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
          />
        );
      default:
        return (
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        );
    }
  };

  const getColors = () => {
    switch (typeKey) {
      case 'depository':
        return 'bg-blue-100 text-blue-600';
      case 'credit':
        return 'bg-orange-100 text-orange-600';
      case 'investment':
        return 'bg-purple-100 text-purple-600';
      case 'loan':
        return 'bg-red-100 text-red-600';
      default:
        return 'bg-gray-100 text-gray-600';
    }
  };

  return (
    <div
      className={cn('flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full', getColors())}
      aria-hidden="true"
    >
      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        {getIcon()}
      </svg>
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

/**
 * ConnectionDetailsModal - Full connection details with sync history and account management
 *
 * Features:
 * - Overview with status, last sync, and force refresh
 * - Account list with visibility toggles
 * - Sync history timeline
 * - Consent expiration warnings
 *
 * @example
 * ```tsx
 * <ConnectionDetailsModal
 *   isOpen={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   connectionId={selectedConnectionId}
 * />
 * ```
 */
export default function ConnectionDetailsModal({
  isOpen,
  onClose,
  connectionId,
  onReconnect,
}: ConnectionDetailsModalProps) {
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [updatingAccountId, setUpdatingAccountId] = useState<string | null>(null);

  // Fetch connection details
  const {
    data: connection,
    isLoading,
    error,
  } = useConnectionDetails(isOpen && connectionId ? connectionId : undefined);

  // Force refresh mutation
  const forceRefreshMutation = useForceRefresh();

  // Account visibility mutation
  const updateVisibilityMutation = useUpdateAccountVisibility();

  const handleForceRefresh = useCallback(() => {
    if (connectionId) {
      forceRefreshMutation.mutate(connectionId);
    }
  }, [connectionId, forceRefreshMutation]);

  const handleToggleVisibility = useCallback(
    (accountId: string, isHidden: boolean) => {
      if (!connectionId) return;
      setUpdatingAccountId(accountId);
      updateVisibilityMutation.mutate(
        { connectionId, accountId, data: { isHidden } },
        {
          onSettled: () => {
            setUpdatingAccountId(null);
          },
        }
      );
    },
    [connectionId, updateVisibilityMutation]
  );

  if (!isOpen || !connectionId) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="connection-details-modal-title"
    >
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 transition-opacity"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100">
              <svg
                className="h-5 w-5 text-blue-600"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25"
                />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <h2
                id="connection-details-modal-title"
                className="text-lg font-semibold text-gray-900 truncate"
              >
                {isLoading ? 'Loading...' : connection?.institutionName || 'Connection Details'}
              </h2>
              {connection && (
                <p className="text-sm text-gray-500">
                  {connection.accountsCount} account{connection.accountsCount !== 1 ? 's' : ''} via{' '}
                  {connection.provider}
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={onClose}
              className="p-2 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
              aria-label="Close modal"
            >
              <XIcon className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Tab bar */}
        {connection && (
          <div className="flex border-b border-gray-200 px-6">
            {(['overview', 'accounts', 'history'] as const).map((tab) => (
              <button
                key={tab}
                type="button"
                onClick={() => setActiveTab(tab)}
                className={cn(
                  'px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-colors capitalize',
                  activeTab === tab
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                )}
              >
                {tab === 'history' ? 'Sync History' : tab}
              </button>
            ))}
          </div>
        )}

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1">
          {isLoading && (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin h-8 w-8 border-2 border-blue-600 border-t-transparent rounded-full" />
            </div>
          )}

          {error && (
            <Alert variant="error">
              Failed to load connection details. Please try again.
            </Alert>
          )}

          {connection && activeTab === 'overview' && (
            <OverviewTab
              connection={connection}
              connectionId={connectionId}
              onForceRefresh={handleForceRefresh}
              isRefreshing={forceRefreshMutation.isPending}
              onRenew={onReconnect}
              onReconnectSuccess={onReconnect}
            />
          )}

          {connection && activeTab === 'accounts' && (
            <AccountsTab
              connection={connection}
              onToggleVisibility={handleToggleVisibility}
              updatingAccountId={updatingAccountId}
            />
          )}

          {connection && activeTab === 'history' && <SyncHistoryTab connection={connection} />}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 flex-shrink-0">
          <div className="flex justify-end">
            <Button type="button" variant="outline" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
